import type { DiagramDocument, DiagramPage } from './types';

export function getActivePage(document: DiagramDocument): DiagramPage {
  return document.pages.find((page) => page.id === document.activePageId) ?? document.pages[0];
}

export function touch(document: DiagramDocument): DiagramDocument {
  return { ...document, updatedAt: new Date().toISOString() };
}

export function updateActivePage(document: DiagramDocument, updater: (page: DiagramPage) => DiagramPage): DiagramDocument {
  return touch({
    ...document,
    pages: document.pages.map((page) => page.id === document.activePageId ? updater(page) : page),
  });
}

export function duplicateDocument(document: DiagramDocument, title = `${document.title} (cópia)`): DiagramDocument {
  const now = new Date().toISOString();
  const pageIds = new Map<string, string>();
  const nodeIds = new Map<string, string>();
  const groupIds = new Map<string, string>();

  for (const page of document.pages) {
    pageIds.set(page.id, crypto.randomUUID());
    for (const node of page.nodes) {
      nodeIds.set(node.id, crypto.randomUUID());
      if (node.groupId && !groupIds.has(node.groupId)) groupIds.set(node.groupId, crypto.randomUUID());
    }
  }

  const pages = document.pages.map((page) => ({
    ...structuredClone(page),
    id: pageIds.get(page.id)!,
    nodes: page.nodes.map((node) => {
      const cloned = structuredClone(node);
      const linkPageId = typeof cloned.data?.linkPageId === 'string' ? cloned.data.linkPageId : '';
      return {
        ...cloned,
        id: nodeIds.get(node.id)!,
        groupId: node.groupId ? groupIds.get(node.groupId) ?? null : null,
        data: linkPageId && pageIds.has(linkPageId) ? { ...cloned.data, linkPageId: pageIds.get(linkPageId)! } : cloned.data,
      };
    }),
    edges: page.edges.map((edge) => ({
      ...structuredClone(edge),
      id: crypto.randomUUID(),
      sourceId: nodeIds.get(edge.sourceId)!,
      targetId: nodeIds.get(edge.targetId)!,
    })),
  }));

  return {
    ...structuredClone(document),
    id: crypto.randomUUID(),
    title,
    activePageId: pageIds.get(document.activePageId) ?? pages[0].id,
    pages,
    createdAt: now,
    updatedAt: now,
  };
}
